import * as Sentry from '@sentry/react'
import * as moment from 'moment'
import * as React from 'react'
import styled, { AnyStyledComponent, createGlobalStyle } from 'styled-components'
import I18n from '../../../../core/i18n'
import { postService, reservationService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button, CheckBox, InputTextArea, InputText, CountryFlagSelect } from '../../../atoms'
import Typography from 'components/atoms/_layouts/Typography'
import { Form } from '../../../molecules'
import StripeCardSelect from '../../../organisms/StripeCardSelect'
import ReservationPickupLocation from './atoms/ReservationPickupLocation'
import {
  getAges,
  getLimitParticipantNote,
  sanitizeReservationParamsForSentry,
} from 'utils/reservation'
import { GuestsCounter } from './atoms/GuestsCounter'
import { InputTextAlt } from 'components/atoms/InputTextAlt'
import SvgIcon from 'components/atoms/SvgIcon'
import {
  getPersonalReservationInfo,
  getReservationStorageData,
  setPersonalReservationInfo,
  updateReservationStorageData,
} from 'utils/reservationSessionStorage'

declare var window: interfaces.IWindow

interface IReservation {
  selectedDate: moment.Moment
  totalPrice: number
  departure: interfaces.IStartTime
}
interface IProps {
  post: interfaces.ICustomPost
  guest: interfaces.IUser
  guests: {
    num_of_adults: number
    num_of_children?: number
    num_of_infants?: number
  }
  isSignedIn?: boolean
  reservation: IReservation
  countries: any
  chargePrice: number
  setChargePrice: React.Dispatch<React.SetStateAction<number>>
  setHasCoupon: React.Dispatch<React.SetStateAction<boolean>>
  setGuests(guests): void
  setIsCompleted(flag: boolean): void
  getParticipantTypePrice(participantType: string): number
  setCreatedReservation(reservation: any): void
}

interface IErrors {
  [key: string]: string | null
}

const FIELDS = {
  other_requests: 'other_requests',
  terms_and_conditions: 'terms_and_conditions',
}

export const ReservationForm: React.FC<IProps> = props => {
  const canInputPickup = props.post.post_type === 'private' && props.post.allow_pickup

  const fields = React.useMemo(() => {
    return canInputPickup
      ? {
          ...FIELDS,
          pickup_location: 'pickup_location',
          pickup_latitude: 'pickup_latitude',
          pickup_longitude: 'pickup_longitude',
        }
      : FIELDS
  }, [canInputPickup])

  const personalReservationInfo = getPersonalReservationInfo()

  const [isProcessing, setIsProcessing] = React.useState(false)
  const [isCalculating, setIsCalculating] = React.useState(false)
  const [errors, setErrors] = React.useState<IErrors>({ terms_and_conditions: null })
  const [getStripeParams, setStripeParamsGetter] = React.useState<any>(null)

  const [couponName, setCouponName] = React.useState<string>(null)
  const [activeCouponName, setActiveCouponName] = React.useState<string | null>(null)

  const [totalPrice, setTotalPrice] = React.useState(0)
  const [zeroPeople, setZeroPeople] = React.useState<boolean>(false)
  const [guestFullname, setGuestFullname] = React.useState<string>(
    personalReservationInfo?.fullname || props.guest?.fullname
  )
  const [guestCountryCode, setGuestCountryCode] = React.useState<string>(
    personalReservationInfo?.country_code || props.guest?.country_code
  )
  const [guestEmail, setGuestEmail] = React.useState<string>(
    personalReservationInfo?.email || props.guest?.email
  )
  const [guestTel, setGuestTel] = React.useState<string>(personalReservationInfo?.tel || '')
  const [referral, setReferral] = React.useState(null)
  const [useRegisteredCard, setUseRegisteredCard] = React.useState(!!props.guest)

  const [isOpenGuestCount, setIsOpenGuestCount] = React.useState(false)
  const guestCount = React.useMemo(() => {
    return reservationService.getGuestCount(props.guests)
  }, [props.guests])

  const ages = getAges(props.post.post_prices)

  const handlePickupLocationChange = React.useCallback(
    pickupInfo => {
      const currentPersonalReservationInfo = getPersonalReservationInfo()

      setPersonalReservationInfo({
        other_requests: currentPersonalReservationInfo?.other_requests || '',
        pickup_location: pickupInfo.pickup_location || '',
        pickup_latitude: pickupInfo.latitude === null ? '' : String(pickupInfo.latitude),
        pickup_longitude: pickupInfo.longitude === null ? '' : String(pickupInfo.longitude),
        pickup_location_selection_error: pickupInfo.selectionError,
        country_code: guestCountryCode || '',
        email: guestEmail || '',
        fullname: guestFullname || '',
        tel: guestTel || '',
      })
    },
    [guestCountryCode, guestEmail, guestFullname, guestTel]
  )

  const handleUpdateForm = (updatedErrors, _, values) => {
    setErrors(updatedErrors)

    if (!values) {
      return
    }

    const updatedPersonalReservationInfo: interfaces.IPersonalReservationInfo = {
      other_requests: values?.other_requests || '',
      pickup_location: canInputPickup ? values?.pickup_location || '' : '',
      pickup_latitude: canInputPickup ? values?.pickup_latitude || '' : '',
      pickup_longitude: canInputPickup ? values?.pickup_longitude || '' : '',
      pickup_location_selection_error: canInputPickup
        ? getPersonalReservationInfo().pickup_location_selection_error
        : false,
      country_code: guestCountryCode || '',
      email: guestEmail || '',
      fullname: guestFullname || '',
      tel: guestTel || '',
    }

    setPersonalReservationInfo(updatedPersonalReservationInfo)
  }

  const handleSubmit = async (_, values) => {
    if (zeroPeople) {
      window.flashMessages.addMessage({
        text: I18n.t('generic.reservations_cannot_be_made_by_zero_people'),
        type: 'error',
      })
      return
    }

    if (!values.terms_and_conditions) {
      setErrors({
        terms_and_conditions: I18n.t('generic.please_agree_with_the_terms_and_conditions'),
      })
      return
    } else {
      setErrors({ terms_and_conditions: null })
    }
    setIsProcessing(true)

    // Sentry 送信用パラメータ
    let sentryReservationContext: { [key: string]: any } = { post_id: props.post.id }

    try {
      const start_at = `${props.reservation.selectedDate.format('YYYY/MM/DD')} ${
        props.reservation.departure?.start_time
      }:00`
      const end_at = `${moment(start_at)
        .add(props.post.duration, 'hours')
        .format('YYYY/MM/DD HH:mm:ss')}`

      let params = {
        reservation: {
          post_start_time_id: props.reservation.departure.post_start_time_id,
          post_calendar_id: props.reservation.departure.post_calendar_id,
          post_id: props.post.id,
          other_requests: values.other_requests,
          start_at,
          end_at,
          coupon_name: activeCouponName,
          ...props.guests,
          ...(canInputPickup
            ? {
                pickup_location: values.pickup_location,
                pickup_latitude: values.pickup_latitude,
                pickup_longitude: values.pickup_longitude,
              }
            : {}),
        },
        user: {
          fullname: guestFullname,
          country_code: guestCountryCode,
          email: guestEmail,
          tel: guestTel,
        },
        useRegisteredCard,
        referral,
        // 画面に表示している請求額。サーバーの再計算結果と異なる場合は決済に進ませない
        displayed_charge_price: props.chargePrice,
      }
      sentryReservationContext = {
        post_id: props.post.id,
        params: sanitizeReservationParamsForSentry(params),
      }
      if (constants.PAYMENT_REQUIRED && typeof getStripeParams === 'function' && totalPrice > 0) {
        try {
          const { secret } = await reservationService.createSecret(params)
          const { paymentIntent, error } = await getStripeParams(secret)

          if (error) {
            window.flashMessages.addMessage({ text: error.message, type: 'error' })
            setIsProcessing(false)
            return
          } else {
            params = { ...params, ...{ paymentIntentId: paymentIntent.id } }
            sentryReservationContext = {
              post_id: props.post.id,
              params: sanitizeReservationParamsForSentry(params),
            }
          }
        } catch (e) {
          // Sentryにエラーを送信
          Sentry.captureException(e, {
            tags: { flow: 'reservation_stripe' },
            extra: sentryReservationContext,
          })
          console.error(e)
          setIsProcessing(false)
          return
        }
      }
      const createReservationResponse = props.guest
        ? await reservationService.createReservation({ ...params }).finally(() => {
            setIsProcessing(false)
          })
        : await reservationService.createReservationByNonMember({ ...params }).finally(() => {
            setIsProcessing(false)
          })
      if (createReservationResponse.reservation) {
        props.setCreatedReservation({
          ...createReservationResponse.reservation,
        })
        props.setIsCompleted(true)
      }
    } catch (error) {
      // Sentryにエラーを送信
      Sentry.captureException(error, {
        tags: { flow: 'reservation_submit' },
        extra: sentryReservationContext,
      })
      setIsProcessing(false)
    }
  }

  const getTotalPrice = async params => {
    const { price } = await postService.getCalculation(props.post.id, params)
    setTotalPrice(price)
    setIsCalculating(false)
  }

  const checkCouponAndSetChargePrice = async (newCouponName?: string) => {
    setActiveCouponName(null)
    props.setHasCoupon(false)
    const categoryID = props.post?.category?.id
    if (!categoryID || !newCouponName) {
      props.setChargePrice(totalPrice)
      return
    }
    const params = {
      coupon_name: newCouponName,
      category_id: categoryID,
      price: totalPrice,
    }
    const { charge_price, flush } = await postService.getChargePrice(params).catch(error => {
      return {
        charge_price: totalPrice,
        flush: null,
      }
    })
    if (flush) {
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      setActiveCouponName(newCouponName)
      props.setHasCoupon(!!newCouponName)
    }
    props.setChargePrice(charge_price)
  }

  const getChargePrice = React.useCallback(async () => {
    checkCouponAndSetChargePrice(activeCouponName)
  }, [activeCouponName, totalPrice])

  React.useEffect(() => {
    if (activeCouponName) {
      getChargePrice()
    } else {
      props.setChargePrice(totalPrice)
    }
  }, [totalPrice])

  React.useEffect(() => {
    setZeroPeople(reservationService.getGuestCount(props.guests) === 0)
    getTotalPrice(props.guests)
  }, [props.guests])

  const maxCount = React.useMemo(() => {
    return props.post.post_type === 'private'
      ? Number(props.post.booking_max_people)
      : props.reservation.departure.available_count || Number(props.post.booking_max_people)
  }, [
    props.post.booking_max_people,
    props.post.post_type,
    props.reservation.departure.available_count,
  ])

  // ----- rewardful ----- //
  const [isReadyRewardful, setIsReadyRewardful] = React.useState<boolean>(
    constants.REWARDFUL_ID && typeof window.rewardful === 'function'
  )

  // React外の処理なので、監視を別途行う
  React.useEffect(() => {
    // IDがない場合、セットアップが完了している場合は何もしない
    if (!constants.REWARDFUL_ID || isReadyRewardful) {
      return
    }

    const checkId = setInterval(() => {
      if (typeof window.rewardful === 'function') {
        setIsReadyRewardful(true)
        clearInterval(checkId)
      }
    }, 500)

    return () => clearInterval(checkId)
  }, [isReadyRewardful])

  // rewardfulの準備ができたら処理開始
  React.useEffect(() => {
    if (isReadyRewardful) {
      window.rewardful('ready', () => {
        setReferral(window.Rewardful.referral)
      })
    }
  }, [isReadyRewardful])

  // ----- rewardful ここまで ----- //

  const updateGuests = newValue => {
    if (isCalculating) {
      return
    }
    setIsCalculating(true)
    props.setGuests({
      num_of_adults: newValue.adults ?? 0,
      num_of_children: newValue.children ?? 0,
      num_of_infants: newValue.infants ?? 0,
    })
  }

  const handleUpdateLocalDataByKey = (
    _key: 'guests',
    value: interfaces.IReservationStorageGuests
  ) => {
    const reservationStorageData = getReservationStorageData()
    updateReservationStorageData({
      initParams: {
        ...reservationStorageData.initParams,
        guests: value,
      },
    })
  }

  const showPersonalDetails = React.useMemo(() => {
    return !props.guest?.country_code || !props.guest?.fullname
  }, [props.guest])
  const isFreeTour = props.post.post_prices.every(price => price.price === 0)
  const hasLimitParticipantTypeGuest = React.useMemo(() => {
    if (props.post?.post_type === 'private') {
      return false
    }
    const limitType = props.post.limit_participant_type
    if (limitType === 'child') {
      return props.guests.num_of_children > 0 || props.guests.num_of_infants > 0
    }
    if (limitType === 'infant') {
      return props.guests.num_of_infants > 0
    }
    return false
  }, [
    props.post?.post_type,
    props.post?.limit_participant_type,
    props.guests.num_of_children,
    props.guests.num_of_infants,
  ])

  const isPrivate = props.post?.post_type === 'private'

  const maximumAccommodate = React.useMemo(() => {
    const bookingMaxPeople = Number(props.post.booking_max_people)
    // プライベートか最大人数よりも在庫が多い場合は非表示
    const notNeedShow = props.reservation.departure.limit_people >= bookingMaxPeople
    if (isPrivate || notNeedShow) {
      return null
    }

    return props.reservation.departure.limit_people
  }, [isPrivate, props.post.booking_max_people, props.reservation.departure.limit_people])

  const handleLogin = async () => {
    const currentUrlParam = new URLSearchParams(window.location.search)
    const buildedRedirectUrl =
      location.origin + location.pathname + `?${currentUrlParam.toString()}`
    const encodedUri = encodeURIComponent(buildedRedirectUrl)

    await new Promise(resolve => setTimeout(resolve, 250))
    location.href = '/users/sign_in' + `?redirect_to=${encodedUri}`
  }

  return (
    <>
      <GlobalStyle />
      <Form fields={fields} handleSubmit={handleSubmit} handleUpdateForm={handleUpdateForm}>
        <S.Reservation>
          <div className="Section">
            <Typography type="subtitle" size="xl">
              Guests
            </Typography>

            <div className="Guests_Pulldown" onClick={() => setIsOpenGuestCount(old => !old)}>
              {guestCount} guests
              <SvgIcon name="keyboard_arrow_down" width={24} height={24} />
            </div>
            <div className="Guest_Counter_Wrapper">
              {isOpenGuestCount && (
                <GuestsCounter
                  align="left"
                  ages={getAges(props.post.post_prices)}
                  post={props.post}
                  isPrivate={isPrivate}
                  guests={{
                    adults: props.guests.num_of_adults,
                    children: props.guests.num_of_children,
                    infants: props.guests.num_of_infants,
                  }}
                  departure={props.reservation.departure}
                  setGuests={updateGuests}
                  setIsOpenGuestCount={setIsOpenGuestCount}
                  handleUpdateLocalDataByKey={handleUpdateLocalDataByKey}
                />
              )}
            </div>
            {props.post.limit_participant_type && (
              <Typography type="subtitle" size="lg" color="caution">
                {getLimitParticipantNote(props.post, ages)}
              </Typography>
            )}
            <p className="MaxAttendees">
              {props.post.post_type === 'group'
                ? `${
                    maximumAccommodate
                      ? I18n.t('post.group_tour_description_max', {
                          max_people: maximumAccommodate,
                        })
                      : ''
                  }`
                : `${I18n.t('post.private_tour_description_max', {
                    min_people: props.post.min_people,
                    max_people: maxCount,
                  })}`}
            </p>
          </div>
          <div className="Section">
            <Typography type="subtitle" size="xl">
              Your Requests
            </Typography>
            <div>
              <InputTextArea
                required={false}
                name="other_requests"
                defaultValue={personalReservationInfo?.other_requests || ''}
                error={errors.other_requests}
              />
            </div>
          </div>
          {showPersonalDetails && (
            <div>
              <Typography type="subtitle" size="xl">
                Personal Details
              </Typography>

              {!props.guest?.fullname && (
                <div className="FormRow FormRowFirst">
                  <InputTextAlt
                    required={true}
                    name="guest_fullname"
                    label={I18n.t('generic.name')}
                    defaultValue={guestFullname}
                    error={errors.guest_fullname}
                    onChangeHandler={e => {
                      setGuestFullname(e.target.value)
                    }}
                  />
                </div>
              )}
              {!props.guest && (
                <div className="FormRow">
                  <InputTextAlt
                    required={true}
                    name="guest_email"
                    type="email"
                    label={I18n.t('generic.email')}
                    defaultValue={guestEmail}
                    error={errors.guest_email}
                    onChangeHandler={e => {
                      setGuestEmail(e.target.value)
                    }}
                  />
                </div>
              )}
              {!props.guest?.country_code && (
                <>
                  <div className="FormRow">
                    <CountryFlagSelect
                      required={true}
                      name="guest_country_code"
                      placeholder="Country"
                      countries={props.countries}
                      defaultValue={guestCountryCode}
                      onChangeHandler={setGuestCountryCode}
                    />
                  </div>
                  {errors.guest_country_code && (
                    <span className="Inputs_Error">{errors.guest_country_code}</span>
                  )}
                </>
              )}
              {!props.guest && (
                <>
                  <div className="FormRow FormRowLast">
                    <InputTextAlt
                      required={true}
                      type="tel"
                      name="guest_tel"
                      label="Mobile Phone Number"
                      defaultValue={personalReservationInfo?.tel || ''}
                      onChangeHandler={e => {
                        setGuestTel(e.target.value)
                      }}
                    />
                  </div>
                  {errors.guest_tel && <span className="Inputs_Error">{errors.guest_tel}</span>}
                </>
              )}
            </div>
          )}

          {canInputPickup && (
            <div className="Section">
              <Typography type="subtitle" size="xl">
                Pickup Location
              </Typography>
              <ReservationPickupLocation
                hideTitle={true}
                onPickupLocationChange={handlePickupLocationChange}
                initPickupInfo={{
                  latitude: personalReservationInfo?.pickup_latitude,
                  longitude: personalReservationInfo?.pickup_longitude,
                  pickup_location: personalReservationInfo?.pickup_location,
                  selectionError: personalReservationInfo?.pickup_location_selection_error,
                }}
              />
            </div>
          )}

          {constants.PAYMENT_REQUIRED && totalPrice > 0 && (
            <div className="Section">
              <div className="FlexRow">
                <Typography type="subtitle" size="xl">
                  Payment
                </Typography>
                <img src="/images/stripe-logo-1.svg" className="stripe-logo" alt="Stripe Logo" />
              </div>
              <StripeCardSelect
                setStripeParamsGetter={setStripeParamsGetter}
                setUseRegisteredCard={setUseRegisteredCard}
                useRegisteredCard={useRegisteredCard}
                hideLabel={true}
              />
            </div>
          )}

          {!isFreeTour && (
            <div className="Section">
              <Typography type="subtitle" size="xl">
                Enter a Coupon
              </Typography>
              <div className="CouponInput">
                <InputText
                  large={true}
                  name="coupon_name"
                  placeholder={I18n.t('reservation.coupon_code')}
                  onChangeHandler={e => {
                    setCouponName(e.target.value)
                  }}
                />
                <button
                  type="button"
                  className="CouponButton"
                  onClick={() => checkCouponAndSetChargePrice(couponName)}
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          <div className="TermsAndConditions">
            <div className="TermsCheck">
              <CheckBox
                name="terms_and_conditions"
                defaultChecked={false}
                error={errors.terms_and_conditions}
                showLabelInline={true}
              />
              <label htmlFor="terms_and_conditions">
                I agreee with The{' '}
                <a className="TermsLink" href="/terms_of_use" target="_blank">
                  {I18n.t('generic.terms_and_conditions')}
                </a>
              </label>
            </div>
            {/* TODO: 通常以外で表示しているが、標準旅行約款のときのみ表示する */}
            {props.post.cancel_policy_id && (
              <a
                className={errors.terms_and_conditions ? 'margin' : ''}
                href={I18n.t('generic.yakkan_url')}
                target="_blank"
              >
                {I18n.t('generic.standard_general_conditions')}
              </a>
            )}
          </div>
          <Button
            fullWidth={true}
            disabled={isProcessing || zeroPeople || hasLimitParticipantTypeGuest}
            primary={true}
            largeAlt={true}
          >
            Confirm and Pay
          </Button>
          {!props.isSignedIn && (
            <div className="login_to_proceed" onClick={handleLogin}>
              <span className="bold">Login</span> to Proceed
            </div>
          )}
          <div className="BookInfo">
            <SvgIcon name="info" className="icon" ariaHidden={true} role="presentation" />
            {props.post.instant_booking ? (
              <span>Your order will be confirmed immediately.</span>
            ) : (
              <span>
                Your order will not be confirmed.
                <br />
                We will immediately check the status of your reservation and contact you by message.
              </span>
            )}
          </div>
        </S.Reservation>
      </Form>
    </>
  )
}

const GlobalStyle = createGlobalStyle`
  .header-wrapper, .Scroller, .mainFooter {
    display: none !important;
  }
`

const S: { [key: string]: AnyStyledComponent } = {}

S.Reservation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 12px 0;
  max-width: 560px;

  .back,
  .title {
    font-size: 16px;
  }

  .right {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  h2 {
    font-size: 20px;
  }
  .CouponInput {
    width: 100%;
    display: flex;
    gap: 12px;
    align-items: center;
    > div {
      flex: 1;
    }
    .CouponButton {
      height: 40px;
      padding: 0 16px;
      border-radius: 6px;
      outline: none;
      cursor: pointer;
      line-height: 1;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
      transition: opacity 0.2s;
      height: auto;
      line-height: 2.4rem;
      font-size: 1.4rem;
      padding: 1.6rem;
      border-radius: 1.2rem;
      background-color: #00896c;
      color: #ffffff;
      border: none;
      :hover {
        opacity: 0.6;
      }
    }
  }
  .TermsAndConditions {
    .TermsCheck {
      display: flex;
      align-items: center;
      position: relative;
      .TermsLink {
        color: ${constants.COLORS.black};
        text-decoration: underline;
        &:hover {
          text-decoration: none;
        }
      }
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        font-size: 1.4rem;
      }
      .CheckBox_Error {
        position: absolute;
        margin-top: 0.4rem;
        white-space: nowrap;
      }
    }
    > a {
      display: block;
      margin-top: 1.2rem;
      color: ${constants.COLORS.primary};
      font-size: 1.4rem;
      text-decoration: underline;
      &:hover {
        text-decoration: none;
      }
    }
    .margin {
      margin-top: 4rem;
    }
  }

  .FirstMessage {
  }

  .TotalPrice {
    font-size: 16px;
    padding-top: 12px;
    display: flex;
    align-items: end;
    justify-content: space-between;
  }

  .TotalPrice_Label {
    font-size: 16px;
    font-weight: bold;
  }

  .TotalPrice_Wrapper {
    margin-top: 8px;
    display: flex;
    align-items: center;
  }

  .TotalPrice_Price {
    margin: 0 8px;
    font-size: 20px;
    &.discounted {
      font-size: 16px;
      text-decoration-line: line-through;
    }
  }

  .couponName {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 24px 0 16px;
    .CouponLabel {
      margin-right: 16px;
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        width: 100%;
        font-size: 14px;
        margin-bottom: 8px;
      }
    }
    input {
      margin-right: 16px;
      padding: 8px 12px;
      border: solid 1px ${constants.COLORS.divider};
      border-radius: 4px;
      font-size: 15px;
      transition: border 0.2s ease;
      outline: none;
      background: none;

      &:not([readonly]):focus {
        border: solid 1px ${constants.COLORS.primary};
      }
    }
  }

  .MaxPeople {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 400px;
    margin-top: 24px;
  }

  .MaxPeople_Label {
    font-size: 14px;
    font-weight: bold;

    > span {
      display: block;
      font-weight: normal;
    }
  }
  .Section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    .MaxAttendees {
      font-size: 1.4rem;
    }
    textarea {
      border-radius: 1.2rem;
    }
  }

  .Guests_Pulldown {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    width: 100%;
    max-width: 232px;
    padding: 16px 24px;
    border-radius: 12px;
    border: 1px solid ${constants.COLORS.divider};
    transition: border-color 0.2s;
    cursor: pointer;
    &:hover {
      border-color: ${constants.COLORS.primary};
    }
  }
  .Guest_Counter_Wrapper {
    margin-top: -12px;
    position: relative;
  }

  .County_Phone {
    display: flex;
    gap: 12px;
    align-items: center;
    .Country {
      width: 33%;
    }
    .Phone {
      width: 67%;
    }
  }
  .Inputs_Error {
    display: block;
    margin-top: 8px;
    color: ${constants.COLORS.cautionText};
    font-size: 14px;
  }

  .FlexRow {
    width: 100%;
    display: flex;
    gap: 12px;
    align-items: center;
  }
  .stripe-logo {
    width: auto;
    height: 2.4rem;
  }

  .Panel {
    background-color: ${constants.COLORS.white};
    max-width: 880px;
    margin: 0 auto;
  }

  .ButtonContainer {
    display: flex;
    align-items: center;
    margin-top: 24px;
    .Button {
      flex: 0 0 auto;
      margin-top: 0;
      margin-right: 10px;
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        margin-top: 16px;
      }
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      flex-direction: column-reverse;
      align-items: flex-start;
    }
  }

  .BookInfo {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    color: ${constants.COLORS.subText};
    font-size: 12px;
    .icon {
      flex: 0 0 auto;
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }
  }

  .FormRow {
    position: relative;
    padding: 12px 0 0;
  }
  .confirmText {
    position: absolute;
    bottom: -2px;
    font-size: 12px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      top: 25px;
      left: 110px;
      bottom: auto;
    }
  }
  .login_to_proceed {
    font-size: 14px;
    font-weight: normal;
    text-align: center;
    cursor: pointer;
    color: ${constants.COLORS.primary};
    .bold {
      font-weight: bold;
    }
    &:hover {
      text-decoration: underline;
    }
  }
`
